import { Component } from "react"

import { Button, Container, Spinner, Table  } from "react-bootstrap"
import { toast } from "react-toastify"
import { BehaviorSubject, concat, of, Subscription } from "rxjs"
import { catchError, distinctUntilChanged, map, switchMap } from "rxjs/operators"

import { GetCurrentServiceHistory, ServiceHistoryGridResponse } from "../../../api/OrderResults/CurrentServiceHistory"
import OrderResultRecord from "../../../api/OrderResults/OrderResultModel/OrderResultRecord"
import FormatDate from "../../../formatters/FormatDate"
import FormatError from "../../../formatters/FormatError"
import TablePagination from "../../TablePagination"
import EventDetailsModal from "../EventDetailsModal"

interface CurrentServiceHistoryProps {
  PersonID: number
  PoolID?: number
  PoolName?: string
  isOntrack?: boolean
}

interface CurrentServiceHistoryState {
  loading: boolean
  data?: ServiceHistoryGridResponse
  detailsModal: boolean
  details?: OrderResultRecord
}

const PageSize = 10

interface GridParams {
  pageNumber: number
}

export default class CurrentServiceHistory extends Component<CurrentServiceHistoryProps, CurrentServiceHistoryState> {
  state: CurrentServiceHistoryState = {
    loading: false,
    detailsModal: false,
  }

  page$ = new BehaviorSubject<GridParams>({
    pageNumber: 1,
  })

  pageSubscription?: Subscription

  componentDidMount() {
    const { PersonID, PoolID } = this.props

    this.setState({ loading: true })

    this.pageSubscription = this.page$
      .pipe(
        distinctUntilChanged(),
        switchMap((params: GridParams) =>
          concat(
            of({ loading: true }),
            GetCurrentServiceHistory({ PersonID, PoolID, PageNumber: params.pageNumber, PageSize }).pipe(
              map((result: ServiceHistoryGridResponse) => ({ data: result, loading: false })),
              catchError((error) => of({ loading: false, errorMessage: FormatError(error) }))
            )
          )
        )
      )
      .subscribe(
        (newState: { loading: boolean; data?: ServiceHistoryGridResponse; errorMessage?: string }) => {
          if (newState.data) {
            this.setState({ loading: newState.loading, data: newState.data })
          } else {
            this.setState({ loading: newState.loading })
          }
          if (newState.errorMessage) {
            toast.error(newState.errorMessage)
          }
        },
        (error) => {
          this.setState({ loading: false })
          toast.error(FormatError(error))
        }
      )
  }

  componentWillUnmount() {
    this.pageSubscription?.unsubscribe()
  }

  handlePageChange = (pageNumber: number) => this.page$.next({ ...this.page$.value, pageNumber })

  showDetailsModal = (details: OrderResultRecord) => this.setState({ detailsModal: true, details })

  render() {
    const { loading, data, detailsModal, details } = this.state
    const { PoolID, PoolName, isOntrack } = this.props

    return (
      <div>
        {loading && <Spinner animation="border" size="sm" />}
        {detailsModal && details && (
          <EventDetailsModal details={details} onHide={() => this.setState({ detailsModal: false })} />
        )}
        {!loading && data && (
          <Container fluid className="grid-container event-grid">
            {PoolID && PoolID > 0 && <h5>Pool: {PoolName}</h5>}
            <Table bordered responsive>
              <thead>
                <tr>
                  <th>SERVICE</th>
                  <th>STATUS</th>
                  <th>REASON</th>
                  <th>RESULT</th>
                  <th>COMPANY</th>
                  {!isOntrack && <th>REGULATED</th>}
                  <th>REFERENCE ID</th>
                  <th>EVENT DATE</th>
                  <th>REPORT DATE</th>
                </tr>
              </thead>
              <tbody>
                {data.Records.length > 0 &&
                  data.Records.map((row: OrderResultRecord) => (
                    <tr key={`${row.TestDetailID  }_${ row.TestDetailClinicalID}`}>
                      <td className="service-name">{row.Service}</td>
                      <td className="test-reason">{row.Status}</td>
                      <td className="test-reason">{row.Reason}</td>
                      <td className="test-result">{row.Result}</td>
                      <td className="service-name">{row.CompanyName}</td>
                      {!isOntrack && <td>{row.Regulated}</td>}
                      <td className="reference-id">
                        <Button variant="link" onClick={() => this.showDetailsModal(row)} className="p-0">
                          {row.ReferenceID}
                        </Button>
                      </td>
                      <td>{FormatDate(row.EventDate)}</td>
                      <td>{FormatDate(row.ReportDate)}</td>
                    </tr>
                  ))}
                {data.Records.length === 0 && (
                  <tr>
                    <td colSpan={9}>No Records Found</td>
                  </tr>
                )}
                {data.Records.length > 0 && (
                  <tr>
                    <td colSpan={9}>
                      <span className="total-records">Total records: {data.Total}</span>
                      <TablePagination
                        pageNumber={this.page$.value.pageNumber}
                        pages={data.Pages}
                        onChange={this.handlePageChange}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Container>
        )}
      </div>
    )
  }
}
